import { useQuery } from '@tanstack/react-query';
import Axios, { type AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  usePublicAxiosInstance,
} from 'shared/hooks/utils';
import apiService from 'shared/service/api.service';
import urlJoin from 'url-join';
import { z } from 'zod/v4';

const URL = urlJoin(apiService.baseUrl, 'debtor_portal_api/v1/legacy_links');

const INVALID_LINK_STATUSES: ReadonlyArray<number> = [401, 404];

// #region Variables
const LoadLegacyPortalLinkVariablesSchema = z.object({
  invoiceId: z.string(),
  token: z.string(),
});

export type LoadLegacyPortalLinkVariables = z.infer<typeof LoadLegacyPortalLinkVariablesSchema>;
// #endregion Variables

// #region Response
const LegacyPortalLinkSchema = z.object({ url: z.url() });

export type LegacyPortalLink = { url: string | null };
// #endregion Response

// MARK: Load function
async function loadLegacyPortalLinkQueryFn(
  axiosInstance: AxiosInstance,
  { invoiceId, token }: LoadLegacyPortalLinkVariables,
): Promise<LegacyPortalLink> {
  try {
    const { data } = await axiosInstance.get(URL, { params: { invoice_id: invoiceId, token } });

    const parseResult = LegacyPortalLinkSchema.safeParse(data);
    if (!parseResult.success) {
      handleZodError(parseResult, 'legacyPortalLink');
      throw new SilentError();
    }

    return parseResult.data;
  } catch (error) {
    // An unknown invoice or a wrong token is an expired link, not an error: the global handler would log a 401 out
    if (Axios.isAxiosError(error) && INVALID_LINK_STATUSES.includes(error.response?.status ?? 0))
      return { url: null };
    throw error;
  }
}

// MARK: Hook
export function useLoadLegacyPortalLink(
  variables: LoadLegacyPortalLinkVariables,
  options: QueryOptions<LegacyPortalLink> = {},
) {
  const axiosInstance = usePublicAxiosInstance();

  const queryResult = useQuery<LegacyPortalLink, ApiError>({
    queryKey: ['legacyPortalLink', variables],
    queryFn: () => loadLegacyPortalLinkQueryFn(axiosInstance, variables),
    ...options,
  });

  return addResourceNameToQueryResult<LegacyPortalLink, ApiError, 'legacyPortalLink'>(
    'legacyPortalLink',
    queryResult,
  );
}
